import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { Select, TextField } from '.';
import { theme } from '../../theme/theme';

const usePropertyClasses = makeStyles({
    root: {
        display: 'flex',
    },
    display: {
        flexDirection: 'column',
        color: theme.colors.neutral[1],
        '& > span:first-of-type': {
            ...theme.typography.content.caption.paragraph,
            marginBottom: 5,
            textTransform: 'capitalize',
        },
        '& > span:last-of-type': {
            ...theme.typography.content.text,
            marginBottom: 0,
            whiteSpace: 'pre-wrap',
        },
    },
    input: {
        '& .MuiFormLabel-root': {
            textTransform: 'capitalize',
        },
    },
});

/**
 * Small element that switches between a text or select field, and labels, to edit or display names and values.
 *
 * This component also has a small enter key functionality, that triggers blur on the component and consequently a
 * `props.onChange` call.
 * This is only enabled in text fields that are not multiline.
 *
 * @param props.className component classes applied on the root div in all property modes
 * @param props.name property name
 * @param props.value property value
 * @param props.multiline multiline option used by text input elements
 * @param props.rows number of rows to display when `props.multiline`
 * @param props.options list of available options, if provided the component becomes an select when editable
 * @param props.optionEquals options comparator, defaults to strict comparison
 * @param props.renderOption function used to transform the value and options into a react node
 * @param props.unselect unselect option element, the change value is `undefined`, it is only displayed if `props.value`
 *        or `props.defaultValue` is not present in `props.options`
 * @param props.placeholder input placeholder, not shown when `!props.editable`
 * @param props.error show an error string in the component, only in edit mode
 * @param props.snake transform `props.name` from snake_case to normal case
 * @param props.editable if the input or select should be rendered, allowing the value to be edited
 * @param props.onChange callback triggered when the value changes and the input is blurred, if options is provided, the
 *        value argument will have `T` type, otherwise string
 */
export const Property = <T extends any>(props: {
    className?: string;
    name?: string;
    value?: T;
    multiline?: boolean;
    rows?: number;
    options?: readonly T[];
    optionEquals?: (a?: T, b?: T) => boolean;
    renderOption?: (option: T) => React.ReactNode;
    placeholder?: string;
    error?: string;
    unselect?: React.ReactNode;
    snake?: boolean;
    editable?: boolean;
    onChange?: (name: string, value?: T | string) => void;
}) => {
    const classes = usePropertyClasses();
    const displayName = props.snake ? props.name?.replace(new RegExp('_', 'g'), ' ') : props.name;
    const defaultValue = React.useMemo(() => props.value, []);
    const optionEquals = props.optionEquals ?? ((a?: T, b?: T) => a === b);
    const renderOption = props.renderOption ?? String;
    const labelId = Math.random().toString();

    return !props.editable ? (
        <div className={clsx(classes.root, classes.display, props.className)}>
            <span id={labelId}>{displayName}</span>
            <span aria-labelledby={labelId}>{props.value == undefined ? '' : renderOption(props.value)}</span>
        </div>
    ) : props.options ? (
        <Select
            className={clsx(classes.root, classes.input, props.className)}
            label={displayName}
            defaultValue={defaultValue}
            options={props.options}
            optionEquals={optionEquals}
            renderOption={renderOption}
            error={props.error != undefined}
            helperText={props.error}
            unselect={props.unselect}
            onValue={(option?: T) => props.onChange?.(props.name ?? '', option)}
        />
    ) : (
        <TextField
            className={clsx(classes.root, classes.input, props.className)}
            label={displayName}
            defaultValue={defaultValue}
            multiline={props.multiline}
            rows={props.rows}
            rowsMax={Infinity}
            placeholder={props.placeholder}
            error={props.error != undefined}
            helperText={props.error}
            onKeyPress={event => event.key === 'Enter' && !props.multiline && (event.target as HTMLInputElement).blur()}
            onBlur={event => props.onChange?.(props.name ?? '', event.target.value)}
        />
    );
};
